import { getDirectRelationship } from '@cognite/apm-client';
import type { Checklist, ChecklistItem, FdmFile } from '@cognite/apm-client';
import type { DirectRelationReference } from '@cognite/sdk';

import { extractSourceableFields } from '../shared-utils/sourceable-utils';

import type {
  CDMChecklistItemUpsertType,
  CDMChecklistUpsertType,
} from './cdm-types';

/**
 * Maps a Checklist to a CDMChecklistUpsertType for upsert, stripping
 * fields that are not part of the instance write:
 * - `createdBy`, `updatedBy` — deprecated InFieldUser fields excluded to prevent
 *   PII from being written to data model storage (see extractSourceableFields).
 * - `createdTime`, `lastUpdatedTime` — server-managed; excluded from writes.
 * - `checklistItems` — written separately via the edge relationships service.
 * @param checklist - The checklist to map.
 * @param rootLocation - The root location of the checklist.
 * @param appInstanceSpace - The space of the app instance.
 * @returns A payload ready for writing to the Cognite data model via the sourceable upsert service. */
export const mapChecklistToUpsertType = (
  checklist: Checklist,
  rootLocation: DirectRelationReference,
  appInstanceSpace: string
): CDMChecklistUpsertType => {
  return {
    ...extractSourceableFields(checklist),
    externalId: checklist.externalId,
    title: checklist.title,
    description: checklist.description,
    type: checklist.type,
    status: checklist.status,
    startTime: checklist.startTime,
    endTime: checklist.endTime,
    assignedTo: checklist.assignedTo,
    isArchived: checklist.isArchived,
    rootLocation:
      checklist.rootLocation && checklist.rootLocation.space
        ? {
            space: checklist.rootLocation.space,
            externalId: checklist.rootLocation.externalId,
          }
        : rootLocation,
    solutionTags: checklist.solutionTags
      ?.filter((tag) => tag.externalId)
      .map((tag) => ({
        externalId: tag.externalId,
        space: appInstanceSpace,
      })),
  };
};

/**
 * Maps a ChecklistItem to a CDMChecklistItemUpsertType for upsert, stripping
 * fields that are not part of the instance write:
 * - `createdBy`, `updatedBy` — deprecated InFieldUser fields excluded to prevent
 *   PII from being written to data model storage (see extractSourceableFields).
 * - `createdTime`, `lastUpdatedTime` — server-managed; excluded from writes.
 * - `observations`, `measurements`, `conditionalActions` — written separately via the edge relationships service.
 * @param checklistItem - The checklist item to map.
 * @param appInstanceSpace - The space of the app instance.
 * @returns A payload ready for writing to the Cognite data model via the sourceable upsert service. */
export const mapChecklistItemToUpsertType = (
  checklistItem: ChecklistItem,
  appInstanceSpace: string
): CDMChecklistItemUpsertType => {
  return {
    ...extractSourceableFields(checklistItem),
    externalId: checklistItem.externalId,
    title: checklistItem.title,
    description: checklistItem.description,
    labels: checklistItem.labels,
    order: checklistItem.order,
    status: checklistItem.status,
    note: checklistItem.note,
    startTime: checklistItem.startTime,
    endTime: checklistItem.endTime,
    asset: getDirectRelationship(checklistItem.asset),
    files: checklistItem.files
      ?.filter((file): file is FdmFile => Boolean(file?.externalId))
      .map((file) => ({
        externalId: file.externalId,
        space: file.space || appInstanceSpace, // '' is invalid per API; treat as missing
      })),
  };
};
